import './Modals.scss';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Input, Modal, Select } from 'antd';
import { Company, Department, ModalProps, User } from '../../utils/types';
import SearchInput from '../Search/SearchInput';
import {
  fetchData,
  getErrorMessage,
  Notify,
  postData,
  toDropdownListFormat,
  updateData,
} from '../../utils/constants';

type DepartmentModalProps = ModalProps & {
  department: Department | null;
  refreshData: (name: string) => Promise<void>;
};

const DepartmentModal = ({
  isOpen,
  setIsOpen,
  department,
  refreshData,
}: DepartmentModalProps) => {
  const [managers, setManagers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);

  const initManager = useCallback(
    (managerName: string | null) => {
      if (!managerName || !managers) return;

      const normalized = (name: string) => name.trim().toLowerCase();

      const manager = managers.find((m) => {
        const fullName1 = `${m.firstName} ${m.lastName}`;
        const fullName2 = `${m.lastName} ${m.firstName}`;
        return (
          normalized(fullName1) === normalized(managerName) ||
          normalized(fullName2) === normalized(managerName)
        );
      });

      if (!manager) return;

      setManager(manager);
      setManagerSearchValue(`${manager.firstName} ${manager.lastName}`);
    },
    [managers],
  );

  const initCompany = useCallback(
    (companyName: string) => {
      if (!companyName || !companies) return;

      const company = companies.find((c) => c.name === companyName);
      if (!company) return;

      setCompany(company.id);
    },
    [companies],
  );

  const initParentDept = useCallback(
    (parentName: string | null) => {
      if (!parentName || !departments) {
        setParentDept(null);
        return;
      }

      const dept = departments.find((d) => d.nameDepartment === parentName);
      if (!dept) {
        setParentDept(null);
        return;
      }

      setParentDept(dept.id);
    },
    [departments],
  );

  const [name, setName] = useState<string>(
    department ? department.nameDepartment : '',
  );
  const [manager, setManager] = useState<User | null>(null);
  const [managerSearchValue, setManagerSearchValue] = useState('');
  const [company, setCompany] = useState<number | ''>('');
  const [parentDept, setParentDept] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen === false) {
      return;
    }
    fetchData(
      `${process.env.REACT_APP_API_URL}/users/displayAllUsers`,
      setManagers,
    );
    fetchData(
      `${process.env.REACT_APP_API_URL}/companies/getAllCompanies`,
      setCompanies,
    );
  }, [isOpen]);

  useEffect(() => {
    if (isOpen === false) {
      return;
    }
    fetchData(
      `${process.env.REACT_APP_API_URL}/departments/allAllDepartments`,
      setDepartments,
    );
  }, [companies, isOpen]);

  const handleSubmit = useCallback(async () => {
    const isEdit = !!department?.id;
    const formData = {
      ...(isEdit ? { id: department.id } : {}),
      nameDepartment: name,
      managerId: manager?.id,
      companyId: company || null,
      parentDepartmentId: parentDept,
    };

    const url = isEdit
      ? `${process.env.REACT_APP_API_URL}/departments/updateDepartmentById/${department.id}`
      : `${process.env.REACT_APP_API_URL}/departments/addNewDepartment`;

    try {
      isEdit ? await updateData(url, formData) : await postData(url, formData);
      Notify(
        'success',
        `Department ${isEdit ? 'updated' : 'created'} successfully!`,
      );
      setIsOpen(false);
      await refreshData('all');
    } catch (error) {
      console.error(`${isEdit ? 'PUT' : 'POST'} error:`, error);
      Notify(
        'error',
        getErrorMessage(
          error,
          `Oops! Something went wrong while ${isEdit ? 'updating' : 'creating'} department.`,
        ),
      );
    }
  }, [company, department, manager, name, parentDept, refreshData, setIsOpen]);

  useEffect(() => {
    if (!isOpen) {
      setName('');
      setManager(null);
      setManagerSearchValue('');
      setCompany('');
      setParentDept(null);
      return;
    }

    if (department) {
      setName(department.nameDepartment);
      initCompany(department.companyName);
      initParentDept(department.parentDepartmentName);
      initManager(department.managerName);
    }
  }, [isOpen, department, initCompany, initManager, initParentDept]);

  return (
    <Modal
      className="md"
      title={department?.id ? 'Update department' : 'Add new department'}
      open={isOpen}
      onCancel={() => setIsOpen(false)}
      footer={[
        <Button key="cancel" onClick={() => setIsOpen(false)}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
        >
          {department?.id ? 'Update department' : 'Create department'}
        </Button>,
      ]}
    >
      <Form layout="vertical">
        <Form.Item label="Name">
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Form.Item>
        <Form.Item label="Manager">
          <SearchInput
            onSelect={(u) => {
              setManager(u);
              setManagerSearchValue(u ? `${u.firstName} ${u.lastName}` : '');
            }}
            options={managers}
            toDisplay={['firstName', 'lastName']}
            value={managerSearchValue}
            onChangeValue={setManagerSearchValue}
          />
        </Form.Item>
        <Form.Item label="Company">
          <Select
            value={company}
            options={toDropdownListFormat(companies, 'name', 'id').map(
              (item) => ({
                label: item.text,
                value: item.value,
              }),
            )}
            onChange={(value) => setCompany(value as number)}
          />
        </Form.Item>
        <Form.Item label="Parent Department">
          <Select
            value={parentDept ?? undefined}
            options={toDropdownListFormat(
              departments,
              'nameDepartment',
              'id',
            ).map((item) => ({
              label: item.text,
              value: item.value,
            }))}
            allowClear
            placeholder="No parent department"
            onChange={(value) =>
              setParentDept(typeof value === 'number' ? value : null)
            }
            disabled={companies.length === 0}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default DepartmentModal;
